import { z } from 'zod';

export const deckScopeSchema = z
  .object({
    sectionSlug: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .optional(),
    topicSlug: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .optional(),
    unitSlug: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .optional(),
  })
  .strict()
  .superRefine((scope, context) => {
    if (scope.unitSlug && !scope.sectionSlug)
      context.addIssue({
        code: 'custom',
        message: 'Unit scope needs a section',
      });
    if (scope.topicSlug && !scope.unitSlug)
      context.addIssue({ code: 'custom', message: 'Topic scope needs a unit' });
  });

export type DeckScope = z.infer<typeof deckScopeSchema>;

/** Stable identity for a deck page/study scope across equivalent object values. */
export function deckScopeKey(scope?: DeckScope): string {
  return JSON.stringify([
    scope?.sectionSlug ?? null,
    scope?.unitSlug ?? null,
    scope?.topicSlug ?? null,
  ]);
}

export const deckSummarySchema = z
  .object({
    description: z.string(),
    id: z.uuidv7(),
    isPublic: z.boolean(),
    language: z.string(),
    slug: z.string(),
    title: z.string(),
  })
  .strict();

export type DeckSummary = z.infer<typeof deckSummarySchema>;

const reservedDeckSlugs = new Set(['api', 'decks', 'pdf', 'status']);

export function isCanonicalDeckSlug(value: string): boolean {
  return (
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) && !reservedDeckSlugs.has(value)
  );
}
